import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, PaperPlaneTilt, ChatCircle, Gear, CurrencyDollar, CheckCircle } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    inquiryType: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const inquiryTypes = [
    { value: 'general', label: 'General Inquiries', icon: ChatCircle, description: 'Questions about MenoPath and our services' },
    { value: 'technical', label: 'Technical Support', icon: Gear, description: 'Help with your account or technical issues' },
    { value: 'sales', label: 'Sales', icon: CurrencyDollar, description: 'Pricing, partnerships, and business inquiries' }
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleInquirySelect = (type) => {
    setFormData({ ...formData, inquiryType: type });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API_URL}/contact`, {
        name: formData.name,
        email: formData.email,
        inquiry_type: formData.inquiryType,
        subject: formData.subject,
        message: formData.message
      });
      setIsSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
      console.error('Contact form error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-[#F8F6FF]">
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-white/70 backdrop-blur-xl border-b border-white/40">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="text-2xl font-bold text-[#5B21B6] font-['Outfit']" data-testid="nav-logo">
              MenoPath
            </Link>
            <Link to="/">
              <button className="flex items-center gap-2 text-[#5B21B6] hover:text-[#4C1D95] transition-colors font-medium">
                <ArrowLeft className="w-5 h-5" />
                Back to Home
              </button>
            </Link>
          </div>
        </nav>

        {/* Success Message */}
        <main className="pt-28 pb-20 px-6 flex items-center justify-center min-h-screen">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center max-w-md"
          >
            <div className="w-20 h-20 rounded-full bg-[#D1FAE5] flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-[#059669]" weight="fill" />
            </div>
            <h1 className="text-3xl font-bold text-[#1E1B4B] font-['Outfit'] mb-4">
              Message Sent!
            </h1>
            <p className="text-[#6B7280] mb-8">
              Thank you for reaching out. Our team will get back to you within 24-48 hours.
            </p>
            <Link to="/">
              <Button className="bg-[#5B21B6] hover:bg-[#4C1D95] rounded-full px-8">
                Return to Home
              </Button>
            </Link>
          </motion.div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F6FF]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/70 backdrop-blur-xl border-b border-white/40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold text-[#5B21B6] font-['Outfit']" data-testid="nav-logo">
            MenoPath
          </Link>
          <Link to="/">
            <button className="flex items-center gap-2 text-[#5B21B6] hover:text-[#4C1D95] transition-colors font-medium">
              <ArrowLeft className="w-5 h-5" />
              Back to Home
            </button>
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-28 pb-12 px-6 bg-gradient-to-br from-[#5B21B6] to-[#7C3AED] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl" />
        </div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white font-['Outfit'] leading-tight">
              Contact Us
            </h1>
            <p className="mt-4 text-xl text-white/90 max-w-2xl mx-auto">
              We're here to help. Choose your inquiry type and send us a message.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form */}
      <main className="py-12 px-6">
        <div className="max-w-3xl mx-auto">
          <motion.form 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            onSubmit={handleSubmit}
            className="glass-card rounded-3xl p-8 md:p-12"
          >
            {/* Inquiry Type Selection */}
            <div className="mb-8">
              <label className="block text-lg font-semibold text-[#1E1B4B] font-['Outfit'] mb-4">
                What can we help you with?
              </label>
              <div className="grid sm:grid-cols-3 gap-4">
                {inquiryTypes.map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => handleInquirySelect(type.value)}
                    className={`p-4 rounded-2xl border-2 transition-all duration-300 text-left ${
                      formData.inquiryType === type.value
                        ? 'border-[#5B21B6] bg-[#EDE9FE]'
                        : 'border-gray-200 hover:border-[#5B21B6]/50 bg-white'
                    }`}
                  >
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 ${
                      formData.inquiryType === type.value
                        ? 'bg-[#5B21B6]'
                        : 'bg-[#F3F4F6]'
                    }`}>
                      <type.icon 
                        className={`w-6 h-6 ${
                          formData.inquiryType === type.value ? 'text-white' : 'text-[#5B21B6]'
                        }`} 
                        weight="duotone" 
                      />
                    </div>
                    <h3 className={`font-semibold mb-1 ${
                      formData.inquiryType === type.value ? 'text-[#5B21B6]' : 'text-[#1E1B4B]'
                    }`}>
                      {type.label}
                    </h3>
                    <p className="text-xs text-[#6B7280]">{type.description}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Name & Email */}
            <div className="grid sm:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-2">
                  Your Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#5B21B6] focus:ring-2 focus:ring-[#5B21B6]/20 outline-none transition-all"
                  placeholder="Jane Smith"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#5B21B6] focus:ring-2 focus:ring-[#5B21B6]/20 outline-none transition-all"
                  placeholder="jane@example.com"
                />
              </div>
            </div>

            {/* Subject */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-[#374151] mb-2">
                Subject <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#5B21B6] focus:ring-2 focus:ring-[#5B21B6]/20 outline-none transition-all"
                placeholder="How can we help?"
              />
            </div>

            {/* Message */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-[#374151] mb-2">
                Message <span className="text-red-500">*</span>
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={5}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#5B21B6] focus:ring-2 focus:ring-[#5B21B6]/20 outline-none transition-all resize-none"
                placeholder="Tell us more about your inquiry..."
              />
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isSubmitting || !formData.inquiryType}
              className="w-full bg-[#5B21B6] hover:bg-[#4C1D95] rounded-xl h-14 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Sending...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <PaperPlaneTilt className="w-5 h-5" weight="bold" />
                  Send Message
                </span>
              )}
            </Button>

            {!formData.inquiryType && (
              <p className="text-center text-sm text-[#6B7280] mt-4">
                Please select an inquiry type above to continue
              </p>
            )}
          </motion.form>

          {/* Response Time Note */}
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-center text-sm text-[#6B7280] mt-6"
          >
            We typically respond within 24-48 business hours
          </motion.p>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#1E1B4B] py-8 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-white/50 text-sm">© 2026 MenoPath. All rights reserved.</p>
          <div className="mt-4 flex flex-wrap justify-center gap-6">
            <Link to="/about" className="text-white/70 hover:text-white transition-colors text-sm">About</Link>
            <Link to="/contact" className="text-white/70 hover:text-white transition-colors text-sm">Contact</Link>
            <Link to="/terms" className="text-white/70 hover:text-white transition-colors text-sm">Terms of Service</Link>
            <Link to="/privacy" className="text-white/70 hover:text-white transition-colors text-sm">Privacy Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
